import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../AuthContext';

type Summary = {submittedAt: string | null; status: string | null};
type InviteResult = {sent: boolean; sendError?: string | null; manualUrl?: string};
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function BrokerProfileInviteAction({brokerId, brokerName}: {
  brokerId: string; brokerName: string;
}) {
  const {roleId} = useAuth();
  const isAdmin = roleId === 'admin' || roleId === 'super-admin';
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setHistoryError(false);
    httpsCallable(functions, 'brokerProfileSummary')({brokerId})
      .then((result) => { if (active) setSummary(result.data as Summary); })
      .catch(() => { if (active) setHistoryError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [brokerId, isAdmin]);
  const send = async () => {
    setBusy(true);
    setMessage('');
    setManualUrl('');
    try {
      const result = (await httpsCallable(functions, 'brokerProfileInvite')({brokerId})).data as InviteResult;
      if (result.sent) setMessage(`Profile link sent to ${brokerName} on WhatsApp.`);
      else {
        setMessage(result.sendError || 'WhatsApp could not send the profile link.');
        setManualUrl(result.manualUrl || '');
      }
    } catch (error: any) {
      setMessage(error?.message || 'Could not create the profile link.');
    } finally {
      setBusy(false);
    }
  };
  const date = summary?.submittedAt ? new Date(summary.submittedAt) : null;
  const when = date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat('en-IN', {timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'}).format(date)
    : null;
  const status = summary?.status === 'pending' ? 'Pending approval'
    : summary?.status === 'approved' ? 'Approved'
      : summary?.status === 'rejected' ? 'Rejected' : summary?.status;
  if (!isAdmin) return null;
  return <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25}}>
    <Button onClick={send} disabled={busy || loading} variant="outlined" size="small"
      sx={{borderRadius: 999, borderColor: '#d9d3c8', color: '#9d6f32', fontFamily: FONT, fontWeight: 700, textTransform: 'none'}}>
      {busy ? 'Sending…' : 'Send broker profile form'}
    </Button>
    <Typography sx={{fontFamily: FONT, fontSize: 11.5, color: '#5b5b5b'}}>
      {loading ? 'Checking profile history…' : historyError ? 'Profile history unavailable' : summary?.submittedAt ? `Filled ${when || 'previously'} · ${status || 'Submitted'}` : 'No profile submitted yet'}
    </Typography>
    {message && <Typography role="status" sx={{fontFamily: FONT, fontSize: 11.5, color: manualUrl ? '#8a5317' : '#2f6b35'}}>{message}</Typography>}
    {manualUrl && <Box component="a" href={manualUrl} target="_blank" rel="noopener noreferrer"
      sx={{fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#9d6f32'}}>Open WhatsApp to send link</Box>}
  </Box>;
}
